import { createTheme, createStyles, makeStyles, MuiThemeProvider } from "@material-ui/core";
import { KeyboardDatePicker } from "@material-ui/pickers";
import { ParsableDate } from "@material-ui/pickers/constants/prop-types";
import { MaterialUiPickersDate } from "@material-ui/pickers/typings/date";
import "date-fns";
import React from "react";
import { useStore } from "../../hooks/useStore";
import { useTranslation } from "../../hooks/useTranslation";

const useStyles = makeStyles(() =>
  createStyles({
    formControl: {
      margin: 0,
      "MuiFormHelperText-root.Mui-error": {
        opacity: 0.5,
      },
    },
    error: {
      color: "red",
      fontSize: "1rem",
    },
  })
);

type DatePickerProps = {
  id: string;
  name: string;
  value: ParsableDate;
  label: string;
  size: any;
  placeholder?: string;
  height?: number;
  onchange: (date: MaterialUiPickersDate, value?: string | null | undefined) => void;
  maxDate?: Date;
  maxDateMessage?: string;
  minDate?: Date;
  minDateMessage?: string;
  inputref?: any;
  tabIndex?: number;
  autoFocus?: boolean;
  errors?: any;
  registration?: any;
  disabled?: boolean;
  className?: string;
  primaryColor?: string;
  inputVariant?: "outlined" | "standard" | "filled";
  margin?: "normal" | "dense" | "none";
  disableUnderline?: boolean;
};

const DatePicker = ({
  size,
  maxDate,
  maxDateMessage,
  minDate,
  minDateMessage,
  id,
  name,
  value,
  label,
  placeholder,
  height,
  tabIndex,
  onchange,
  inputref,
  autoFocus,
  errors,
  registration,
  disabled = false,
  className,
  primaryColor,
  inputVariant = "outlined",
  margin = "normal",
  disableUnderline = false,
}: DatePickerProps) => {
  const mainMuiTheme = createTheme({
    palette: {
      primary: {
        main: primaryColor || "#229C27",
      },
    },
  });
  const { configStore } = useStore();
  const { t, BundleKey } = useTranslation();
  const {
    ref: rhfRef,
    onChange: rhfOnChange,
    onBlur: rhfOnBlur,
    name: rhfName,
  } = registration || {};
  // Chrome puede ignorar autocomplete="off" en campos que interpreta como vencimiento de tarjeta.
  // Igual que TextInput, el input empieza readOnly y se habilita recién al foco real para que el
  // navegador no ofrezca tarjetas guardadas.
  const [isInputReadOnly, setIsInputReadOnly] = React.useState(true);

  const handleInputFocus = (event: React.FocusEvent<HTMLInputElement>) => {
    event.target.readOnly = false;
    setIsInputReadOnly(false);
  };

  const handleInputBlur = (event: React.FocusEvent<HTMLInputElement>) => {
    setIsInputReadOnly(true);
    rhfOnBlur?.(event);
  };

  const classes = useStyles();
  return (
    <>
      <MuiThemeProvider theme={mainMuiTheme}>
        <KeyboardDatePicker
          disabled={disabled}
          name={rhfName || name}
          autoOk
          variant="inline"
          inputVariant={inputVariant}
          autoFocus={autoFocus}
          label={label}
          value={value}
          format={configStore.dateFormat}
          maxDate={maxDate}
          maxDateMessage={maxDateMessage}
          minDate={minDate}
          minDateMessage={minDateMessage}
          placeholder={placeholder || t(BundleKey.INPUT_DATE_PLACEHOLDER)}
          size={size}
          margin={margin}
          id={id}
          InputProps={height ? { style: { minHeight: height }, disableUnderline } : { disableUnderline }}
          inputProps={{
            ...(height ? { style: { minHeight: height } } : {}),
            autoComplete: "off",
            readOnly: isInputReadOnly,
          }}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onChange={(date, value) => {
            if (rhfOnChange) {
              rhfOnChange({ target: { name: rhfName || name, value: date } });
            }
            if (onchange) onchange(date, value);
          }}
          fullWidth
          className={[classes.formControl, className].filter(Boolean).join(" ")}
          InputAdornmentProps={{
            position: "start",
            color: "primary",
            variant: "standard",
          }}
          inputRef={(e) => {
            if (rhfRef) rhfRef(e);
            if (inputref) {
              if (typeof inputref === "function") inputref(e);
              else (inputref as any).current = e;
            }
          }}
          invalidDateMessage={t(BundleKey.ERROR_DATE_INVALID)}
          KeyboardButtonProps={{
            "aria-label": "change date",
            color: "primary",
            tabIndex: tabIndex,
          }}
          FormHelperTextProps={{ className: classes.error }}
        />
        {errors ? <p className={classes.error}>{errors}</p> : null}
      </MuiThemeProvider>
    </>
  );
};
export default DatePicker;
